import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Paper,
  Grid,
  Checkbox,
  FormControlLabel,
  Box,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Autocomplete,
} from "@mui/material";
import { useRef } from "react";

import { useNavigate } from "react-router-dom";
import useSave from "../../Hooks/useSave";
import { toFloat, toInt } from "./utils/parseNumbers";

export default function ProductCreate() {
  const debounceRef = useRef(null);

  const navigate = useNavigate();
  const { fetchGraphql, DialogSave } = useSave(null);
  const [productCategorys, setProductCategorys] = useState([]);
  const [productSubCategorys, setProductSubCategorys] = useState([]);
  const [productUnitOfMeasures, setProductUnitOfMeasures] = useState([]);
  const [productProductionSheets, setProductProductionSheets] = useState([]);
  const [sheetProducts, setSheetProducts] = useState([]);
  const [sheetSearchLoading, setSheetSearchLoading] = useState(false);
  const [selectedSheetProduct, setSelectedSheetProduct] = useState(null);

  const [form, setForm] = useState({
    productId: null,
    sheetParentProductId: null,
    piecesPerBuom: null,
    productCode: "",
    productName: "",
    productDescription: "",
    productPrice: "",
    productCategory: "",
    productSubCategory: "",
    productUnitOfMeasure: "",
    productAmount: "",
    groupId: null,
    productType: "product",
    priceByPound: false,
    minQuantity: null,
    productionSheet: [],
    sortOrder: "",
    product_can_be_purchased: true,
    product_can_be_sold: true,
  });
  useEffect(() => {
    fetchGraphql({
      query: `
      query {
        productCategorys
        productUnitOfMeasures
        productProductionSheets
      }
    `,
    }).then((res) => {
      setProductCategorys(res.data.productCategorys || []);
      setProductUnitOfMeasures(res.data.productUnitOfMeasures || []);
      setProductProductionSheets(res.data.productProductionSheets || []);
    });
  }, []);
  useEffect(() => {
    if (!form.productCategory) return;

    fetchGraphql({
      query: `
      query ($productCategory: String) {
        productSubCategorys(productCategory: $productCategory)
      }
    `,
      variables: { productCategory: form.productCategory },
    }).then((res) => {
      setProductSubCategorys(res.data.productSubCategorys || []);
    });
  }, [form.productCategory]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "productCategory") {
      setForm({
        ...form,
        productCategory: value,
        productSubCategory: "",
      });
      return;
    }
    setForm({
      ...form,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const fetchSheetProducts = (search) => {
    if (!search || search.length < 2) return;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      setSheetSearchLoading(true);

      const res = await fetchGraphql({
        query: `
        query ($search: String!) {
          searchSheetProducts(search: $search) {
            product_id
            product_name
          }
        }
      `,
        variables: { search },
      });

      setSheetProducts(res?.data?.searchSheetProducts || []);
      setSheetSearchLoading(false);
    }, 300);
  };

  const handleSubmit = async () => {
    try {
      await fetchGraphql({
        query: `
        mutation CreateProduct($product: ProductInput) {
          createProduct(product: $product)
        }
      `,
        variables: {
          product: {
            ...form,
            productPrice: form.productPrice ? toFloat(form.productPrice) : null,
            productAmount: form.productAmount
              ? toFloat(form.productAmount)
              : null,

            sortOrder: form.sortOrder ? toInt(form.sortOrder) : null,
            piecesPerBuom: form.piecesPerBuom
              ? toInt(form.piecesPerBuom)
              : null,
            minQuantity: form.minQuantity ? toInt(form.minQuantity) : null,
            sheetParentProductId: form.sheetParentProductId
              ? toInt(form.sheetParentProductId)
              : null,
          },
        },
      });
    } catch (error) {
      alert(error);
      console.log(error);
    }

    navigate("/products/products");
  };

  return (
    <>
      <DialogSave />
      <Box
        sx={{
          overflowY: "auto",
          padding: 3,
        }}
      >
        <Paper sx={{ p: 3, maxWidth: 900, margin: "auto" }}>
          <h2>Create Product</h2>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Product Code"
                name="productCode"
                value={form.productCode}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Product Name"
                name="productName"
                value={form.productName}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="productDescription"
                value={form.productDescription}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                fullWidth
                type="number"
                label="Price"
                name="productPrice"
                value={form.productPrice}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Amount"
                name="productAmount"
                value={form.productAmount}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  name="productCategory"
                  value={form.productCategory}
                  onChange={handleChange}
                >
                  {productCategorys.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel>Sub Category</InputLabel>
                <Select
                  label="Sub Category"
                  name="productSubCategory"
                  value={form.productSubCategory}
                  onChange={handleChange}
                  disabled={!productSubCategorys.length}
                >
                  {productSubCategorys.map((sub) => (
                    <MenuItem key={sub} value={sub}>
                      {sub}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel>Unit of Measure</InputLabel>
                <Select
                  label="Unit of Measure"
                  name="productUnitOfMeasure"
                  value={form.productUnitOfMeasure}
                  onChange={handleChange}
                >
                  {productUnitOfMeasures.map((uom) => (
                    <MenuItem key={uom} value={uom}>
                      {uom}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Pieces per BUOM"
                name="piecesPerBuom"
                value={form.piecesPerBuom}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Minimum Quantity"
                name="minQuantity"
                value={form.minQuantity}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                fullWidth
                type="number"
                label="Sort Order"
                name="sortOrder"
                value={form.sortOrder}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={4}>
              <Autocomplete
                disabled={form.productUnitOfMeasure !== "Sheet"}
                options={sheetProducts}
                value={selectedSheetProduct}
                loading={sheetSearchLoading}
                getOptionLabel={(option) => option.product_name}
                filterOptions={(x) => x}
                onInputChange={(_, value) => fetchSheetProducts(value)}
                onChange={(_, value) => {
                  setSelectedSheetProduct(value);
                  setForm({
                    ...form,
                    sheetParentProductId: value ? value.product_id : null,
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Parent Sheet Product"
                    placeholder="Search sheet products…"
                    fullWidth
                  />
                )}
              />
            </Grid>

            {/* <Grid item xs={4}>
              <TextField
                disabled={form.productUnitOfMeasure !== "Sheet "}
                fullWidth
                type="number"
                label="Product Parent"
                name="productParent"
                value={form.productParent}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid> */}

            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel>Production Sheets</InputLabel>
                <Select
                  multiple
                  label="Production Sheets"
                  name="productionSheet"
                  value={form.productionSheet}
                  onChange={(e) =>
                    setForm({ ...form, productionSheet: e.target.value })
                  }
                  renderValue={(selected) => selected.join(", ")}
                >
                  {productProductionSheets.map((sheet) => (
                    <MenuItem key={sheet} value={sheet}>
                      <Checkbox
                        checked={form.productionSheet.includes(sheet)}
                      />
                      {sheet}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.priceByPound}
                    name="priceByPound"
                    onChange={handleChange}
                  />
                }
                label="Price By Pound"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.product_can_be_purchased}
                    name="product_can_be_purchased"
                    onChange={handleChange}
                  />
                }
                label="Can be Purchased"
              />
            </Grid>

            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.product_can_be_sold}
                    name="product_can_be_sold"
                    onChange={handleChange}
                  />
                }
                label="Can be Sold"
              />
            </Grid>

            <Grid item xs={12}>
              <Button variant="contained" onClick={handleSubmit}>
                Save Product
              </Button>
              <Button
                sx={{ ml: 2 }}
                onClick={() => navigate("/products/products")}
              >
                Cancel
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </>
  );
}
